import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Users, ClipboardList, Archive } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RegisterPlayerDialog } from "@/components/registrations/RegisterPlayerDialog";
import { RegistrationCard } from "@/components/registrations/RegistrationCard";

export default function Players() {
  const { user, featureFlags, featureFlagsLoaded } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], can_manage: false, stages: [], stage_labels: {} });
  const [teams, setTeams] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stage, setStage] = useState("all");
  const [team, setTeam] = useState("all");
  const [age, setAge] = useState("all");
  const [archived, setArchived] = useState(false);
  const isAdmin = user?.role === "admin";
  const view = params.get("view") === "queue" && data.can_manage ? "queue" : "mine";

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/registrations", { params: { include_archived: archived } }), api.get("/teams"), api.get("/kit/catalogue")])
      .then(([r, t, c]) => { setData(r.data); setTeams(t.data || []); setCatalogue(c.data || []); })
      .catch(err => toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load registrations"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (featureFlags?.player_registration) load(); }, [archived, featureFlags?.player_registration]);
  const ages = useMemo(() => Array.from(new Set(data.items.map(r => r.year_group).filter(Boolean))).sort(), [data.items]);

  if (!featureFlagsLoaded) return null;
  if (!featureFlags?.player_registration) return <Navigate to="/app/calendar?disabled=1" replace />;

  const myTeamIds = new Set(teams.filter(t => t.owner_id === user?.id || (t.coach_ids || []).includes(user?.id)).map(t => t.id));
  const myTeams = isAdmin || data.can_manage ? teams : teams.filter(t => myTeamIds.has(t.id));
  const rows = data.items.filter(r => {
    if (view === "mine" && data.can_manage && !isAdmin && myTeamIds.size && !myTeamIds.has(r.team_id) && !myTeamIds.has(r.from_team_id) && r.created_by_id !== user?.id) return false;
    if (archived ? !r.archived : r.archived) return false;
    if (stage !== "all" && (stage === "info_required" ? r.status !== "info_required" : r.stage !== stage)) return false;
    if (team !== "all" && r.team_id !== team && r.from_team_id !== team) return false;
    if (age !== "all" && r.year_group !== age) return false;
    return true;
  });
  const needsAction = data.items.filter(r => !r.archived && (view === "queue" ? r.status === "active" && r.stage !== "coach_approval" : r.status === "info_required" || (r.stage === "coach_approval" && (r.pending_team_ids || []).some(t => myTeamIds.has(t))))).length;
  const onChanged = (upd) => { setData(d => ({ ...d, items: d.items.map(x => x.id === upd.id ? upd : x) })); window.dispatchEvent(new Event("admin-queue-changed")); };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10" data-testid="players-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Players</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">{view === "queue" ? "Registrations queue" : "Player registration"}</h1>
          <p className="opacity-70 text-sm mt-1">{view === "queue" ? `${needsAction} active registration${needsAction === 1 ? "" : "s"} to progress.` : needsAction ? `${needsAction} item${needsAction === 1 ? "" : "s"} need your attention.` : "Register new players, request transfers or dual registrations, and follow club + league sign-up."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data.can_manage && (
            <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="players-view-toggle">
              {[["mine", "My players", Users], ["queue", "Queue", ClipboardList]].map(([k, l, I]) => (
                <button key={k} type="button" onClick={() => setParams(k === "queue" ? { view: "queue" } : {})} data-testid={`players-view-${k}`} className={`px-3 py-2 text-xs tracking-widest uppercase inline-flex items-center gap-1.5 ${view === k ? "bg-[#83B68B]/15 text-[#83B68B]" : "opacity-70 hover:opacity-100"}`}><I className="w-3 h-3" /> {l}</button>
              ))}
            </div>
          )}
          <button type="button" onClick={() => { setEditing(null); setOpen(true); }} className="btn-volt px-5 py-2.5 rounded-md text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2" data-testid="register-player-btn"><UserPlus className="w-4 h-4" /> Register player</button>
        </div>
      </div>

      <div className="card-tactical rounded-md p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end" data-testid="players-filters">
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5">Stage</div>
          <Select value={stage} onValueChange={setStage}><SelectTrigger data-testid="players-filter-stage"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All stages</SelectItem><SelectItem value="info_required">Information required</SelectItem>{data.stages.map(s => <SelectItem key={s} value={s}>{data.stage_labels[s]}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5">Team</div>
          <Select value={team} onValueChange={setTeam}><SelectTrigger data-testid="players-filter-team"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All teams</SelectItem>{myTeams.map(t => <SelectItem key={t.id} value={t.id}>{t.age_group} · {t.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase opacity-60 mb-1.5">Year group</div>
          <Select value={age} onValueChange={setAge}><SelectTrigger data-testid="players-filter-age"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All year groups</SelectItem>{ages.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
        </div>
        {isAdmin && (
          <button type="button" onClick={() => setArchived(a => !a)} data-testid="players-archive-toggle" className={`px-3 py-2 rounded-md border text-xs tracking-widest uppercase inline-flex items-center gap-1.5 justify-center ${archived ? "border-[#83B68B]/60 text-[#83B68B] bg-[#83B68B]/10" : "border-border opacity-70 hover:opacity-100"}`}><Archive className="w-3 h-3" /> {archived ? "Viewing archive" : "Archive"}</button>
        )}
      </div>

      {loading ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
        : rows.length === 0 ? <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="players-empty">{archived ? "Nothing archived yet." : "No registrations here yet."}</div>
        : <div className="space-y-3" data-testid="players-list">{rows.map(r => <RegistrationCard key={r.id} r={r} meta={data} canManage={view === "queue" || isAdmin} myTeamIds={myTeamIds} onChanged={onChanged} onEdit={x => { setEditing(x); setOpen(true); }} />)}</div>}

      <RegisterPlayerDialog open={open} onClose={() => setOpen(false)} onSaved={() => { load(); window.dispatchEvent(new Event("admin-queue-changed")); }} teams={myTeams} clubTeams={teams} catalogue={catalogue} editing={editing} />
    </div>
  );
}
